import React, { useCallback, useEffect, useState } from 'react';
import { Box, Dialog, DialogTitle, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UndoIcon from '@mui/icons-material/Undo';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useHoverNavigation from '../use-hover-navigation';
import { hoverHighlightSx } from '../hover-style';
import AppsActionBarWithSettings from '../apps-action-bar-with-settings';
import {
    GRID_SIZE,
    TILE_COLORS,
    tileTextColor,
    initGrid,
    move,
    addRandomTile,
    canMove,
    hasWon,
} from './game2048-utils';

const CELL_SIZE = 80;
const GAP = 8;

// Hover navigation items: Up(0), Right(1), Down(2), Left(3), ActionBar(4+)
const DIRECTIONS = ['up', 'right', 'down', 'left'];

const arrowBtnSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    borderRadius: 3,
    transition: 'box-shadow 0.3s ease, background-color 0.3s ease',
    bgcolor: 'grey.200',
    '&:hover': { bgcolor: 'grey.300' },
};


export default function Game2048() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [grid, setGrid] = useState(initGrid);
    const [score, setScore] = useState(0);
    const [best, setBest] = useState(0);
    const [won, setWon] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const [popupLabel, setPopupLabel] = useState(null);
    const [actionBarActions, setActionBarActions] = useState([]);

    const itemCount = 4 + actionBarActions.length; // 4 arrows + action bar items

    const onSelect = useCallback((index) => {
        if (index < 4) {
            // Arrow move
            setGrid((prev) => {
                const { grid: next, score: earned, moved } = move(prev, DIRECTIONS[index]);
                if (!moved) return prev;
                const withNew = addRandomTile(next);
                setScore((s) => {
                    const newScore = s + earned;
                    setBest((b) => Math.max(b, newScore));
                    return newScore;
                });
                if (hasWon(withNew) && !won) {
                    setWon(true);
                    setPopupLabel(t('APPLICATIONS.GAME2048.YOU_WIN'));
                }
                if (!canMove(withNew)) {
                    setGameOver(true);
                    setPopupLabel(t('APPLICATIONS.GAME2048.GAME_OVER'));
                }
                return withNew;
            });
        } else {
            // Action bar item clicked
            const actionIndex = index - 4;
            if (actionIndex >= 0 && actionIndex < actionBarActions.length) {
                actionBarActions[actionIndex].onClick();
            }
        }
    }, [t, won, actionBarActions]);

    const { active, activeIndex, paused, pause } = useHoverNavigation(itemCount, onSelect);

    // Pause scanning on popup
    useEffect(() => {
        if (popupLabel !== null) pause();
    }, [popupLabel, pause]);

    // Close popup when scanning resumes
    useEffect(() => {
        if (!paused && popupLabel !== null) {
            setPopupLabel(null);
            if (gameOver) {
                // Restart game
                setGrid(initGrid());
                setScore(0);
                setGameOver(false);
                setWon(false);
            }
        }
    }, [paused, popupLabel, gameOver]);

    const handleRestart = useCallback(() => {
        setGrid(initGrid());
        setScore(0);
        setGameOver(false);
        setWon(false);
        setPopupLabel(null);
    }, []);

    const arrowItems = [
        { icon: ArrowUpwardIcon, label: t('APPLICATIONS.GAME2048.UP') },
        { icon: ArrowForwardIcon, label: t('APPLICATIONS.GAME2048.RIGHT') },
        { icon: ArrowDownwardIcon, label: t('APPLICATIONS.GAME2048.DOWN') },
        { icon: ArrowBackIcon, label: t('APPLICATIONS.GAME2048.LEFT') },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            {/* Action bar */}
            <AppsActionBarWithSettings
                customActions={[]}
                helperActions={[]}
                activeIndex={activeIndex - 4}
                active={active && activeIndex >= 4}
                onActionsChange={setActionBarActions}
                flex="0 0 auto"
            />

            {/* Score bar */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', gap: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        {t('APPLICATIONS.GAME2048.SCORE')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{score}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        {t('APPLICATIONS.GAME2048.BEST')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{best}</Typography>
                </Box>
            </Box>

            {/* Main area: grid + arrows */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, minHeight: 0, px: 2 }}>
                {/* 2048 Grid */}
                <Box
                    sx={{
                        display: 'inline-grid',
                        gridTemplateColumns: `repeat(${GRID_SIZE}, ${CELL_SIZE}px)`,
                        gap: `${GAP}px`,
                        bgcolor: '#bbada0',
                        borderRadius: 2,
                        p: `${GAP}px`,
                    }}
                >
                    {grid.flat().map((value, i) => (
                        <Box
                            key={i}
                            sx={{
                                width: CELL_SIZE,
                                height: CELL_SIZE,
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: value ? (TILE_COLORS[value] || '#3c3a32') : '#cdc1b4',
                                transition: 'background-color 0.15s ease',
                            }}
                        >
                            {value && (
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: value >= 1024 ? 20 : value >= 128 ? 24 : 32,
                                        color: tileTextColor(value),
                                        lineHeight: 1,
                                    }}
                                >
                                    {value}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>

                {/* Arrow controls — cross layout */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateAreas: `
                            ". up ."
                            "left . right"
                            ". down ."
                        `,
                        gridTemplateColumns: '80px 80px 80px',
                        gridTemplateRows: '80px 80px 80px',
                        gap: `${GAP}px`,
                    }}
                >
                    {arrowItems.map((item, idx) => {
                        const Icon = item.icon;
                        const areas = ['up', 'right', 'down', 'left'];
                        return (
                            <Box
                                key={areas[idx]}
                                onClick={() => onSelect(idx)}
                                sx={{
                                    ...arrowBtnSx,
                                    gridArea: areas[idx],
                                    flexDirection: 'column',
                                    ...(active && activeIndex === idx && hoverHighlightSx),
                                }}
                            >
                                <Icon sx={{ fontSize: 36 }} />
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Dialog open={popupLabel !== null} onClose={() => setPopupLabel(null)}>
                <DialogTitle sx={{ textAlign: 'center', px: 8, py: 4, fontSize: '2rem' }}>
                    {popupLabel}
                </DialogTitle>
            </Dialog>
        </Box>
    );
}
